import React, { useEffect, useState } from 'react';
import gql from 'graphql-tag';
import { useLazyQuery, useQuery } from '@apollo/react-hooks';
import { useHistory, useParams } from 'react-router-dom';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import classnames from 'classnames';
import ShowMoreText from 'react-show-more-text';
import { FaSortDown, FaRegCopy } from 'react-icons/fa';

import Select from '../../../components/form/Select';
import Loading from '../../../components/Loading';
import { format, parseISO } from 'date-fns';
import { sorts } from '../../../utils';
import Modal from '../../../components/Modal';
import { usePermissions } from '../../../hooks/usePermissions';
import { userGroups } from '../../../context/AuthContext';

const models = [
    'chunk_subscription',
    'credit_subscription',
    'library',
    'limited_library_circulation',
    'pool_subscription',
    'cost_per_hour',
    'pay_per_circulation',
    'retail',
    'supplemental_credit_subscription',
    'unlimited_subscription',
    'abp'
];

const PLATFORMS_QUERY = gql`
    query {
        __type(name: "AudioEnginePlatform") {
            enumValues {
                name
            }
        }
    }
`;

const AUDIOENGINE_DETAIL = gql`
    fragment Product on AudioEngineBaseProduct {
        active_date
        channel_exclusions
        content_cost
        id
        inactive_date
        isbn
        list_price
        list_price_currency
        publisher_list_price
        publisher_list_price_currency
        modified_date
        sale_start_date
        sale_end_date
        territories
    }
    fragment ABPProduct on AudioEngineABPProduct {
        active_date
        channel_exclusions
        content_cost
        id
        inactive_date
        isbn
        list_price
        list_price_currency
        publisher_list_price
        publisher_list_price_currency
        modified_date
        sale_start_date
        sale_end_date
        territories
    }
    fragment Promotion on AudioEnginePromotion {
        end_date
        id
        invoice_cost
        list_price
        publisher_list_price
        publisher_list_price_currency
        modified_date
        product_id
        promotion_code
        start_date
    }
    fragment Products on AudioEngineProductsGroup {
        retail {
            ...Product
        }
        library {
            ...Product
        }
        unlimited_subscription {
            ...Product
        }
        pool_subscription {
            ...Product
        }
        cost_per_hour {
            ...Product
        }
        pay_per_circulation {
            ...Product
        }
        limited_library_circulation {
            ...Product
        }
        credit_subscription {
            ...Product
        }
        chunk_subscription {
            ...Product
        }
        supplemental_credit_subscription {
            ...Product
        }
        abp {
            ...ABPProduct
        }
    }
    fragment LocalizedProducts on AudioEngineProducts {
        retail {
            ...Product
        }
        library {
            ...Product
        }
        unlimited_subscription {
            ...Product
        }
        pool_subscription {
            ...Product
        }
        cost_per_hour {
            ...Product
        }
        pay_per_circulation {
            ...Product
        }
        limited_library_circulation {
            ...Product
        }
        credit_subscription {
            ...Product
        }
        chunk_subscription {
            ...Product
        }
        supplemental_credit_subscription {
            ...Product
        }
        abp {
            ...ABPProduct
        }
    }
    query AudioEngineBook($contentId: String!, $platformName: AudioEnginePlatform!) {
        audioEngineBook(contentId: $contentId, platformName: $platformName) {
            raw_json
            active_products {
                ...Products
                localized {
                    ...LocalizedProducts
                }
            }
            inactive_products {
                ...Products
                localized {
                    ...LocalizedProducts
                }
            }
            audiobook {
                abridgement
                authors
                awards
                chapterized
                chapters {
                    chapter_number
                    duration
                    part_number
                }
                copyright
                cover_url
                description
                drm_free
                grade_level
                id
                language
                publisher
                metadata_sig
                modified_date
                narrators
                runtime
                sample_url
                series
                street_date
                sub_title
                title
            }
            active_promotions {
                retail {
                    ...Promotion
                }
                localized {
                    retail {
                        ...Promotion
                    }
                }
            }
            inactive_promotions {
                retail {
                    ...Promotion
                }
                localized {
                    retail {
                        ...Promotion
                    }
                }
            }
        }
    }
`;

const CardColumn = ({ title = '', value = '', dataRH = null }) => {
    const [isShowCopied, setIsShowCopied] = useState(false);
    const handleShowCopied = () => {
        setIsShowCopied((c) => !c);
        setTimeout(() => {
            setIsShowCopied((c) => !c);
        }, 1500);
    };

    return (
        <>
            <td data-title={title} className="col-xs-12 col-sm-6 col-md-12 product-table-td">
                {!isShowCopied && (
                    <span>
                        {value}
                        <CopyToClipboard onCopy={handleShowCopied} text={dataRH || value}>
                            <button className="btn btn-link pl-1 pr-0 py-0">{<FaRegCopy />}</button>
                        </CopyToClipboard>
                    </span>
                )}
                {isShowCopied && <span className="copied-to-clipboard-notify">Copied to clipboard!</span>}
            </td>
        </>
    );
};
const TerritoryCardColumn = ({ value = '' }) => {
    const [isShowCopied, setIsShowCopied] = useState(false);
    const handleShowCopied = () => {
        setIsShowCopied((c) => !c);
        setTimeout(() => {
            setIsShowCopied((c) => !c);
        }, 1500);
    };
    return (
        <td data-title="Territories" className="col-lg-12 col-md-12 col-sm-6 col-xs-12">
            {!isShowCopied && (
                <>
                    <CopyToClipboard onCopy={handleShowCopied} text={value}>
                        <button className="btn btn-link pl-1 pr-0 py-0">{<FaRegCopy />}</button>
                    </CopyToClipboard>
                    <ShowMoreText lines={3}>{value}</ShowMoreText>
                </>
            )}
            {isShowCopied && <span className="copied-to-clipboard-notify">Copied to clipboard!</span>}
        </td>
    );
};

const ProductTable = ({ products = [] }) => {
    const [sortBy, setSortBy] = useState({ key: 'sale_start_date', sign: true });
    if (products?.length > 1) {
        if (sortBy.key === 'sale_start_date' || sortBy.key === 'sale_end_date' || sortBy.key === 'active_date') {
            if (!sortBy.sign) {
                products = products.sort((a, b) => {
                    let c = new Date(a[sortBy.key]);
                    let d = new Date(b[sortBy.key]);
                    return c - d;
                });
            } else {
                products = products.sort((a, b) => {
                    let c = new Date(a[sortBy.key]);
                    let d = new Date(b[sortBy.key]);
                    return d - c;
                });
            }
        }
        if (sortBy.key === 'list_price' || sortBy.key === 'content_cost') {
            if (!sortBy.sign) {
                products = products.sort((a, b) => b[sortBy.key] - a[sortBy.key]);
            } else {
                products = products.sort((a, b) => a[sortBy.key] - b[sortBy.key]);
            }
        }
        if (sortBy.key === 'model' || sortBy.key === 'type') {
            if (!sortBy.sign) {
                products = products.sort((a, b) => b[sortBy.key].localeCompare(a[sortBy.key]));
            } else {
                products = products.sort((a, b) => a[sortBy.key].localeCompare(b[sortBy.key]));
            }
        }
    }
    return (
        <div className="table-responsive">
            <table className="table table-sm product-history-table">
                <thead>
                    <tr>
                        <th>
                            {products.length > 1 && (
                                <button
                                    className="btn btn-sm pb-0"
                                    onClick={() =>
                                        setSortBy({ key: 'type', sign: sortBy.key === 'type' ? !sortBy.sign : true })
                                    }
                                >
                                    <span className="txt-bold">Type</span>{' '}
                                    <FaSortDown
                                        className={classnames({
                                            'rotate-sort-button': sortBy.sign && sortBy.key === 'type'
                                        })}
                                    />
                                </button>
                            )}
                            {products.length <= 1 && 'Type'}
                        </th>
                        <th>
                            {products.length > 1 && (
                                <button
                                    className="btn btn-sm pb-0"
                                    onClick={() =>
                                        setSortBy({ key: 'model', sign: sortBy.key === 'model' ? !sortBy.sign : true })
                                    }
                                >
                                    <span className="txt-bold">Model</span>{' '}
                                    <FaSortDown
                                        className={classnames({
                                            'rotate-sort-button': sortBy.sign && sortBy.key === 'model'
                                        })}
                                    />
                                </button>
                            )}
                            {products.length <= 1 && 'Model'}
                        </th>
                        <th>Product ID</th>
                        <th>ISBN</th>
                        <th>
                            {products.length > 1 && (
                                <button
                                    className="btn btn-sm pb-0"
                                    onClick={() =>
                                        setSortBy({
                                            key: 'active_date',
                                            sign: sortBy.key === 'active_date' ? !sortBy.sign : true
                                        })
                                    }
                                >
                                    <span className="txt-bold">Active Date</span>{' '}
                                    <FaSortDown
                                        className={classnames({
                                            'rotate-sort-button': sortBy.sign && sortBy.key === 'active_date'
                                        })}
                                    />
                                </button>
                            )}
                            {products.length <= 1 && 'Active Date'}
                        </th>
                        <th>
                            {products.length > 1 && (
                                <button
                                    className="btn btn-sm pb-0"
                                    onClick={() =>
                                        setSortBy({
                                            key: 'sale_start_date',
                                            sign: sortBy.key === 'sale_start_date' ? !sortBy.sign : true
                                        })
                                    }
                                >
                                    <span className="txt-bold">Sales Start Date</span>{' '}
                                    <FaSortDown
                                        className={classnames({
                                            'rotate-sort-button': sortBy.sign && sortBy.key === 'sale_start_date'
                                        })}
                                    />
                                </button>
                            )}
                            {products.length <= 1 && 'Sales Start Date'}
                        </th>
                        <th>
                            {products.length > 1 && (
                                <button
                                    className="btn btn-sm pb-0"
                                    onClick={() =>
                                        setSortBy({
                                            key: 'sale_end_date',
                                            sign: sortBy.key === 'sale_end_date' ? !sortBy.sign : true
                                        })
                                    }
                                >
                                    <span className="txt-bold">Sales End Date</span>{' '}
                                    <FaSortDown
                                        className={classnames({
                                            'rotate-sort-button': sortBy.sign && sortBy.key === 'sale_end_date'
                                        })}
                                    />
                                </button>
                            )}
                            {products.length <= 1 && 'Sales End Date'}
                        </th>
                        <th>
                            {products.length > 1 && (
                                <button
                                    className="btn btn-sm pb-0"
                                    onClick={() =>
                                        setSortBy({
                                            key: 'list_price',
                                            sign: sortBy.key === 'list_price' ? !sortBy.sign : true
                                        })
                                    }
                                >
                                    <span className="txt-bold">List Price</span>{' '}
                                    <FaSortDown
                                        className={classnames({
                                            'rotate-sort-button': sortBy.sign && sortBy.key === 'list_price'
                                        })}
                                    />
                                </button>
                            )}
                            {products.length <= 1 && 'List Price'}
                        </th>
                        <th>
                            {products.length > 1 && (
                                <button
                                    className="btn btn-sm pb-0"
                                    onClick={() =>
                                        setSortBy({
                                            key: 'content_cost',
                                            sign: sortBy.key === 'content_cost' ? !sortBy.sign : true
                                        })
                                    }
                                >
                                    <span className="txt-bold">Content Cost</span>{' '}
                                    <FaSortDown
                                        className={classnames({
                                            'rotate-sort-button': sortBy.sign && sortBy.key === 'content_cost'
                                        })}
                                    />
                                </button>
                            )}
                            {products.length <= 1 && 'Content Cost'}
                        </th>
                        <th className="pr-24">Territories</th>
                    </tr>
                </thead>
                <tbody>
                    {products.map((prod, idx) => {
                        let currency = prod.list_price_currency || 'USD';
                        return (
                            <tr key={`${prod.isbn}_${prod.model}_${prod.sale_start_date}_${prod.sale_end_date}_${idx}`}>
                                <td data-title="Type" className="pt-2 product-table-td">
                                    {prod.type ? prod.type : 'N/A'}
                                </td>
                                <td data-title="Model" className="pt-2 product-table-td">
                                    {prod.model ? prod.model.split('_').join(' ') : 'N/A'}
                                </td>
                                <CardColumn title="Product ID" value={prod.id ? prod.id : 'N/A'} />
                                <CardColumn title="ISBN" value={prod.isbn ? prod.isbn : 'N/A'} />
                                <CardColumn
                                    title="Active Date"
                                    value={prod.active_date ? format(parseISO(prod.active_date), 'P') : 'N/A'}
                                />
                                <CardColumn
                                    title="Sale Start Date"
                                    value={prod.sale_start_date ? format(parseISO(prod.sale_start_date), 'P') : 'N/A'}
                                />
                                <CardColumn
                                    title="Sale End Date"
                                    value={prod.sale_end_date ? format(parseISO(prod.sale_end_date), 'P') : 'N/A'}
                                />
                                <CardColumn
                                    title="List Price"
                                    value={new window.Intl.NumberFormat('en-US', {
                                        style: 'currency',
                                        currency
                                    }).format(prod.list_price)}
                                />
                                <CardColumn
                                    title="Content Cost"
                                    value={new window.Intl.NumberFormat('en-US', {
                                        style: 'currency',
                                        currency
                                    }).format(prod.content_cost)}
                                />
                                <TerritoryCardColumn value={prod.territories.join(', ')} />
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

const PromotionsTable = ({ promotions = [] }) => {
    const [sortBy, setSortBy] = useState({ key: 'sales_start_date', sign: true });

    if (promotions?.length > 1) {
        if (sortBy.key === 'start_date' || sortBy.key === 'end_date' || sortBy.key === 'active_date') {
            if (!sortBy.sign) {
                promotions = promotions.sort((a, b) => {
                    let c = new Date(a[sortBy.key]);
                    let d = new Date(b[sortBy.key]);
                    return c - d;
                });
            } else {
                promotions = promotions.sort((a, b) => {
                    let c = new Date(a[sortBy.key]);
                    let d = new Date(b[sortBy.key]);
                    return d - c;
                });
            }
        }
        if (sortBy.key === 'list_price' || sortBy.key === 'invoice_cost') {
            if (!sortBy.sign) {
                promotions = promotions.sort((a, b) => b[sortBy.key] - a[sortBy.key]);
            } else {
                promotions = promotions.sort((a, b) => a[sortBy.key] - b[sortBy.key]);
            }
        }
        if (sortBy.key === 'model' || sortBy.key === 'promotion_code' || sortBy.key === 'type') {
            if (!sortBy.sign) {
                promotions = promotions.sort((a, b) => b[sortBy.key].localeCompare(a[sortBy.key]));
            } else {
                promotions = promotions.sort((a, b) => a[sortBy.key].localeCompare(b[sortBy.key]));
            }
        }
    }

    return (
        <div className="table-responsive">
            <table className="table table-sm product-history-table">
                <thead>
                    <tr>
                        <th>
                            {promotions.length > 1 && (
                                <button
                                    className="btn btn-sm pb-0"
                                    onClick={() =>
                                        setSortBy({ key: 'type', sign: sortBy.key === 'type' ? !sortBy.sign : true })
                                    }
                                >
                                    <span className="txt-bold">Type</span>{' '}
                                    <FaSortDown
                                        className={classnames({
                                            'rotate-sort-button': sortBy.sign && sortBy.key === 'type'
                                        })}
                                    />
                                </button>
                            )}
                            {promotions.length <= 1 && 'Type'}
                        </th>
                        <th>
                            {promotions.length > 1 && (
                                <button
                                    className="btn btn-sm pb-0"
                                    onClick={() =>
                                        setSortBy({ key: 'model', sign: sortBy.key === 'model' ? !sortBy.sign : true })
                                    }
                                >
                                    <span className="txt-bold">Model</span>{' '}
                                    <FaSortDown
                                        className={classnames({
                                            'rotate-sort-button': sortBy.sign && sortBy.key === 'model'
                                        })}
                                    />
                                </button>
                            )}
                            {promotions.length <= 1 && 'Model'}
                        </th>
                        <th>Product ID</th>
                        <th>
                            {promotions.length > 1 && (
                                <button
                                    className="btn btn-sm pb-0"
                                    onClick={() =>
                                        setSortBy({
                                            key: 'promotion_code',
                                            sign: sortBy.key === 'promotion_code' ? !sortBy.sign : true
                                        })
                                    }
                                >
                                    <span className="txt-bold">Promotion Code</span>{' '}
                                    <FaSortDown
                                        className={classnames({
                                            'rotate-sort-button': sortBy.sign && sortBy.key === 'promotion_code'
                                        })}
                                    />
                                </button>
                            )}
                            {promotions.length <= 1 && 'Promotion Code'}
                        </th>
                        <th>
                            {promotions.length > 1 && (
                                <button
                                    className="btn btn-sm pb-0"
                                    onClick={() =>
                                        setSortBy({
                                            key: 'start_date',
                                            sign: sortBy.key === 'start_date' ? !sortBy.sign : true
                                        })
                                    }
                                >
                                    <span className="txt-bold">Sales Start Date</span>{' '}
                                    <FaSortDown
                                        className={classnames({
                                            'rotate-sort-button': sortBy.sign && sortBy.key === 'start_date'
                                        })}
                                    />
                                </button>
                            )}
                            {promotions.length <= 1 && 'Sales Start Date'}
                        </th>
                        <th>
                            {promotions.length > 1 && (
                                <button
                                    className="btn btn-sm pb-0"
                                    onClick={() =>
                                        setSortBy({
                                            key: 'end_date',
                                            sign: sortBy.key === 'end_date' ? !sortBy.sign : true
                                        })
                                    }
                                >
                                    <span className="txt-bold">Sales End Date</span>{' '}
                                    <FaSortDown
                                        className={classnames({
                                            'rotate-sort-button': sortBy.sign && sortBy.key === 'end_date'
                                        })}
                                    />
                                </button>
                            )}
                            {promotions.length <= 1 && 'Sales End Date'}
                        </th>
                        <th>
                            {promotions.length > 1 && (
                                <button
                                    className="btn btn-sm pb-0"
                                    onClick={() =>
                                        setSortBy({
                                            key: 'list_price',
                                            sign: sortBy.key === 'list_price' ? !sortBy.sign : true
                                        })
                                    }
                                >
                                    <span className="txt-bold">List Price</span>{' '}
                                    <FaSortDown
                                        className={classnames({
                                            'rotate-sort-button': sortBy.sign && sortBy.key === 'list_price'
                                        })}
                                    />
                                </button>
                            )}
                            {promotions.length <= 1 && 'List Price'}
                        </th>
                        <th>
                            {promotions.length > 1 && (
                                <button
                                    className="btn btn-sm pb-0"
                                    onClick={() =>
                                        setSortBy({
                                            key: 'invoice_cost',
                                            sign: sortBy.key === 'invoice_cost' ? !sortBy.sign : true
                                        })
                                    }
                                >
                                    <span className="txt-bold">Invoice Cost</span>{' '}
                                    <FaSortDown
                                        className={classnames({
                                            'rotate-sort-button': sortBy.sign && sortBy.key === 'invoice_cost'
                                        })}
                                    />
                                </button>
                            )}
                            {promotions.length <= 1 && 'Invoice Cost'}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {promotions.map((prod, idx) => {
                        let currency = prod.list_price_currency || 'USD';

                        return (
                            <tr key={`${prod.isbn}_${prod.model}_${prod.start_date}_${prod.end_date}_${idx}`}>
                                <CardColumn title="Type" value={prod.type} />
                                <CardColumn title="Model" value={prod.model} />
                                <CardColumn title="Product ID" value={prod.product_id} />
                                <CardColumn title="Promotion Code" value={prod.promotion_code} />
                                <CardColumn
                                    title="Start Date"
                                    value={format(parseISO(prod.start_date), 'P') || 'N/A'}
                                />
                                <CardColumn title="End Date" value={format(parseISO(prod.end_date), 'P') || 'N/A'} />
                                <CardColumn
                                    title="List Price"
                                    value={new window.Intl.NumberFormat('en-US', {
                                        style: 'currency',
                                        currency
                                    }).format(prod.list_price)}
                                />
                                <CardColumn
                                    title="Invoice Cost"
                                    value={new window.Intl.NumberFormat('en-US', {
                                        style: 'currency',
                                        currency
                                    }).format(prod.invoice_cost)}
                                />
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

function ProductHistory({ contentId, bisacCodes, ...rest }) {
    const history = useHistory();
    const params = useParams();

    const [isJsonModalOpen, setIsJsonModalOpen] = useState(false);

    const isSpotifyUser = usePermissions(userGroups.SPOTIFY);

    const term = isSpotifyUser ? 'platform_spotify' : params.platform || '';

    const [audioEngineDetail, { loading, data, error }] = useLazyQuery(AUDIOENGINE_DETAIL);
    const { loading: platformsLoading, data: platforms } = useQuery(PLATFORMS_QUERY, { skip: isSpotifyUser });

    const handlePlatformChange = (event) => {
        history.push(`/title-management/${contentId}/product-history/${event.target.value}`);
    };

    const [isShowCopied, setIsShowCopied] = useState(false);
    const handleShowCopied = () => {
        setIsShowCopied((c) => !c);
        setTimeout(() => {
            setIsShowCopied((c) => !c);
        }, 1500);
    };
    useEffect(() => {
        if (term) {
            audioEngineDetail({
                variables: { contentId: contentId, platformName: term }
            });
        }
        document.querySelector('.product-history') &&
            document.querySelector('.product-history').classList.add('active');
        return function cleanup() {
            document.querySelector('.product-history') &&
                document.querySelector('.product-history').classList.remove('active');
        };
    }, [contentId, audioEngineDetail, term]);

    if (platformsLoading) return <Loading />;

    const map = {
        activeProducts: {
            label: 'Active Products',
            array: [],
            key: 'active_products',
            type: 'products'
        },
        activePromotions: {
            label: 'Active Promotions',
            array: [],
            key: 'active_promotions',
            type: 'promotions'
        },
        inactiveProducts: {
            label: 'Inactive Products',
            array: [],
            key: 'inactive_products',
            type: 'products'
        },
        inactivePromotions: {
            label: 'Inactive Promotions',
            array: [],
            key: 'inactive_promotions',
            type: 'promotions'
        }
    };

    // model (library vs. retail) and type (localized vs. non-localized) are not included within each promo
    // or product object. We add them here so that each object matches its corresponding table structure
    const mergeProductsOrPromos = (array = [], mod = '', typ = '') => {
        return array.map((item) => ({
            ...item,
            model: mod,
            type: typ
        }));
    };

    // iteratively updates the raw_json returned by the backend to create a new set of arrays
    // which match the table structure of each (active/inactive products and active/inactive promotions)
    if (data?.audioEngineBook?.raw_json) {
        // data returned by the backend is unreliable, but the raw_json is
        // we use the rawJson for all data displayed (on this page only)
        const books_rawJson = JSON.parse(data.audioEngineBook.raw_json);

        models.forEach((model) => {
            for (const property in map) {
                const obj = map[property];
                const array = obj.array;
                const table = books_rawJson?.[obj.key];
                const nonLocalized = table?.[model];
                const localized = table?.localized?.[model];
                if (!!nonLocalized) {
                    array.push(...mergeProductsOrPromos(nonLocalized, model, 'non-localized'));
                }
                if (!!localized) {
                    array.push(...mergeProductsOrPromos(localized, model, 'localized'));
                }
            }
        });
    }

    return (
        <>
            <h3 className="my-4">Product History</h3>

            {!loading && !isSpotifyUser && (
                <p className="mb-6 txt-italic">This service is not available for Ebsco or 24Symbols</p>
            )}
            {!isSpotifyUser && (
                <div className="w-64 mb-12">
                    <Select
                        name="partnerSelect"
                        id="partnerSelect"
                        value={term}
                        onChange={handlePlatformChange}
                        label="Partner"
                    >
                        <option value="">Select Platform...</option>

                        {platforms.__type.enumValues
                            .sort((a, b) => sorts.asc(a.name, b.name))
                            .filter((p) => p?.name !== 'platform_24symbols' && p?.name !== 'platform_ebsco')
                            .map((platform) => (
                                <option key={platform.name} value={platform.name}>
                                    {platform.name.split('_').splice(1).join(' ')}
                                </option>
                            ))}
                    </Select>
                </div>
            )}
            {loading && <Loading />}
            {!loading && term && !error && (
                <div>
                    {data && (
                        <button
                            className="btn btn-secondary mb-4"
                            type="button"
                            onClick={() => setIsJsonModalOpen(true)}
                        >
                            View JSON
                        </button>
                    )}
                    <p className="mb-12">Note: Click the copy icon for copy to clipboard functionality.</p>

                    {Object.keys(map)?.map((key) => {
                        const array = map[key].array;
                        const label = map[key].label;
                        const type = map[key].type;

                        return (
                            <div key={key} className="mb-8">
                                <h4 className="mb-4">{label}</h4>
                                {!array.length && <p>{`No ${label.toLowerCase()}`}</p>}
                                {!!array.length && (
                                    <>
                                        {type === 'products' && <ProductTable products={array} />}
                                        {type === 'promotions' && <PromotionsTable promotions={array} />}
                                    </>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
            {!loading && error && (
                <div className="flash border-danger txt-danger">
                    <p>
                        <strong>Message: </strong>
                        {error?.message}
                    </p>
                </div>
            )}
            {isJsonModalOpen && data && data?.audioEngineBook?.raw_json && (
                <Modal
                    visible={isJsonModalOpen}
                    onClose={() => setIsJsonModalOpen(false)}
                    innerClassName="product-history-json-modal"
                    label="product history json"
                >
                    {!isShowCopied && (
                        <CopyToClipboard
                            onCopy={handleShowCopied}
                            text={JSON.stringify(JSON.parse(data?.audioEngineBook?.raw_json), null, 2)}
                        >
                            <button className="btn btn-link pl-1">{<FaRegCopy />} Copy Json</button>
                        </CopyToClipboard>
                    )}
                    {isShowCopied && <span className="copied-to-clipboard-notify">Copied to clipboard!</span>}
                    <pre>
                        <code>{JSON.stringify(JSON.parse(data?.audioEngineBook?.raw_json), null, 2)}</code>
                    </pre>
                </Modal>
            )}
        </>
    );
}

export default ProductHistory;
